import { useState, useEffect } from "react";
import { Row, Col, Card, message, Button, Tooltip } from "antd";
import { PlusOutlined, DatabaseOutlined } from "@ant-design/icons";
import {
    get_ai_configurations,
    create_ai_configuration,
    update_ai_configuration,
    delete_ai_configuration,
    toggle_ai_configuration,
    test_ai_configuration,
    get_resolved_test_content,
    update_test_content_library,
    seed_test_content_defaults,
} from "../api/admin.apis";
import { AiProviderTable, CreateAiConfigModal, EditAiConfigModal, AiPlaygroundModal } from "../components/ai-provider-management";

function AiProviderManagement() {
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);

    // create
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);

    // edit
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editLoading, setEditLoading] = useState(false);

    // toggle
    const [togglingKey, setTogglingKey] = useState(null);

    // playground
    const [playgroundModalVisible, setPlaygroundModalVisible] = useState(false);
    const [playgroundLoading, setPlaygroundLoading] = useState(false);
    const [savePlaygroundLoading, setSavePlaygroundLoading] = useState(false);
    const [testResult, setTestResult] = useState(null);
    // Library-resolved test content for the open feature.
    // Shape: { id, en_content, ar_content, title, description, is_default, source }.
    // `id` is the ai_test_content_library row this playground edits/saves
    // against; absent only if the resolve call failed.
    const [testContentItem, setTestContentItem] = useState(null);
    const [testContentLoading, setTestContentLoading] = useState(false);
    const [saveTestContentLoading, setSaveTestContentLoading] = useState(false);

    // seed-defaults action
    const [seedingDefaults, setSeedingDefaults] = useState(false);

    useEffect(() => {
        fetchConfigs();
    }, []);

    const fetchConfigs = async () => {
        try {
            setLoading(true);
            const response = await get_ai_configurations();
            if (response.data.status === "success") {
                const data = response.data.data;
                const list = Array.isArray(data) ? data : data.configurations || data.items || [];
                setConfigs(list.map((c, i) => ({ ...c, key: c._id || c.id || i })));
            } else {
                message.error("Failed to load AI configurations");
            }
        } catch (error) {
            console.error(error);
            message.error("Error loading AI configurations");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateConfig = async (values) => {
        try {
            setCreateLoading(true);
            const response = await create_ai_configuration(values);
            if (response.data.status === "success") {
                message.success("AI configuration created successfully");
                setCreateModalVisible(false);
                fetchConfigs();
            } else {
                message.error(response.data.message || "Failed to create configuration");
            }
        } catch (error) {
            console.error(error);
            message.error("Failed to create AI configuration");
        } finally {
            setCreateLoading(false);
        }
    };

    const handleEditOpen = (config) => {
        setSelectedConfig(config);
        setEditModalVisible(true);
    };

    const handleEditConfig = async (values) => {
        try {
            setEditLoading(true);
            const response = await update_ai_configuration(selectedConfig.feature_name, values);
            if (response.data.status === "success") {
                message.success("AI configuration updated successfully");
                setEditModalVisible(false);
                setSelectedConfig(null);
                fetchConfigs();
            } else {
                message.error(response.data.message || "Failed to update configuration");
            }
        } catch (error) {
            console.error(error);
            message.error("Failed to update AI configuration");
        } finally {
            setEditLoading(false);
        }
    };

    const handleDeleteConfig = async (config) => {
        try {
            const response = await delete_ai_configuration(config.feature_name);
            if (response.data.status === "success") {
                message.success("AI configuration deleted successfully");
                fetchConfigs();
            } else {
                message.error(response.data.message || "Failed to delete configuration");
            }
        } catch (error) {
            console.error(error);
            message.error("Failed to delete AI configuration");
        }
    };

    const handlePlaygroundOpen = async (config) => {
        setSelectedConfig(config);
        setTestResult(null);
        setTestContentItem(null);
        setPlaygroundModalVisible(true);
        await loadTestContent(config.feature_name);
    };

    const loadTestContent = async (feature_name) => {
        try {
            setTestContentLoading(true);
            const response = await get_resolved_test_content(feature_name);
            if (response.data.status === "success" && response.data.data) {
                // Resolver auto-seeds from the static file on first read, so
                // `id` should always be present here unless no seed exists at
                // all for this feature.
                setTestContentItem(response.data.data);
            } else {
                setTestContentItem(null);
                message.warning(response.data.message || "No test content available for this feature");
            }
        } catch (error) {
            console.error("Failed to resolve test content", error);
            setTestContentItem(null);
            message.error(error.response?.data?.message || "Failed to load test content");
        } finally {
            setTestContentLoading(false);
        }
    };

    const handleSaveTestContent = async ({ en_content, ar_content }) => {
        if (!testContentItem?.id) {
            message.error("No library row to save against — try reopening the playground");
            return;
        }
        try {
            setSaveTestContentLoading(true);
            const payload = {};
            if (en_content !== undefined) payload.en_content = en_content;
            if (ar_content !== undefined) payload.ar_content = ar_content;
            const response = await update_test_content_library(testContentItem.id, payload);
            if (response.data.status === "success" && response.data.data?.content) {
                const updated = response.data.data.content;
                setTestContentItem((prev) => ({
                    ...(prev || {}),
                    id: updated.id ?? prev?.id,
                    en_content: updated.en_content ?? prev?.en_content,
                    ar_content: updated.ar_content ?? prev?.ar_content,
                    title: updated.title ?? prev?.title,
                    description: updated.description ?? prev?.description,
                    is_default: updated.is_default ?? prev?.is_default,
                }));
                message.success("Test content saved");
            } else {
                message.error(response.data.message || "Failed to save test content");
            }
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.message || "Failed to save test content");
        } finally {
            setSaveTestContentLoading(false);
        }
    };

    const handleSeedDefaults = async () => {
        try {
            setSeedingDefaults(true);
            const response = await seed_test_content_defaults();
            if (response.data.status === "success") {
                const report = response.data.data || {};
                const seeded = report.seeded?.length ?? 0;
                const skipped = report.skipped_already_present?.length ?? 0;
                const failed = report.failed?.length ?? 0;
                message.success(`Seeded ${seeded}, skipped ${skipped}, failed ${failed}`);
            } else {
                message.error(response.data.message || "Failed to seed defaults");
            }
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.message || "Failed to seed test content defaults");
        } finally {
            setSeedingDefaults(false);
        }
    };

    const handleRunTest = async (payload) => {
        try {
            setPlaygroundLoading(true);
            const response = await test_ai_configuration(payload);
            const data = response.data;
            // Outer wrapper: { status: "success", data: { test_id, success, ai_response, ... } }
            if (data.status === "success" && data.data) {
                setTestResult(data.data);
            } else {
                setTestResult({ success: false, error_message: data.message || "Unexpected response format", ai_response: null });
            }
        } catch (error) {
            console.error(error);
            setTestResult({
                success: false,
                error_message: error.response?.data?.message || error.message || "Request failed",
                ai_response: null,
            });
        } finally {
            setPlaygroundLoading(false);
        }
    };

    const handleSavePlaygroundConfig = async (payload) => {
        try {
            setSavePlaygroundLoading(true);
            const response = await update_ai_configuration(selectedConfig.feature_name, payload);
            if (response.data.status === "success") {
                message.success("Configuration saved successfully");
                fetchConfigs();
            } else {
                message.error(response.data.message || "Failed to save configuration");
            }
        } catch (error) {
            console.error(error);
            message.error("Failed to save configuration");
        } finally {
            setSavePlaygroundLoading(false);
        }
    };

    const handleToggleConfig = async (config) => {
        try {
            setTogglingKey(config.feature_name);
            const response = await toggle_ai_configuration(config.feature_name, !config.enabled);
            if (response.data.status === "success") {
                message.success(`Configuration ${config.enabled ? "disabled" : "enabled"} successfully`);
                fetchConfigs();
            } else {
                message.error(response.data.message || "Failed to toggle configuration");
            }
        } catch (error) {
            console.error(error);
            message.error("Failed to toggle AI configuration");
        } finally {
            setTogglingKey(null);
        }
    };

    return (
        <div className="tabled">
            <Row gutter={[24, 0]}>
                <Col xs={24} xl={24}>
                    <Card
                        bordered={false}
                        className="criclebox tablespace mb-24"
                        title={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span>AI Provider Management ({configs.length} configurations)</span>
                                <div style={{ display: "flex", gap: 8 }}>
                                    <Tooltip title="Idempotently copy every static-file preset into the editable library. Already-seeded features are left untouched.">
                                        <Button
                                            icon={<DatabaseOutlined />}
                                            loading={seedingDefaults}
                                            onClick={handleSeedDefaults}
                                        >
                                            Seed Test Content
                                        </Button>
                                    </Tooltip>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => setCreateModalVisible(true)}
                                    >
                                        Add New Configuration
                                    </Button>
                                </div>
                            </div>
                        }
                    >
                        <AiProviderTable
                            configs={configs}
                            loading={loading}
                            onEdit={handleEditOpen}
                            onDelete={handleDeleteConfig}
                            onToggle={handleToggleConfig}
                            togglingKey={togglingKey}
                            onPlayground={handlePlaygroundOpen}
                        />
                    </Card>
                </Col>
            </Row>

            <CreateAiConfigModal
                visible={createModalVisible}
                loading={createLoading}
                onSubmit={handleCreateConfig}
                onClose={() => setCreateModalVisible(false)}
            />

            <EditAiConfigModal
                visible={editModalVisible}
                loading={editLoading}
                config={selectedConfig}
                onSubmit={handleEditConfig}
                onClose={() => {
                    setEditModalVisible(false);
                    setSelectedConfig(null);
                }}
            />

            <AiPlaygroundModal
                visible={playgroundModalVisible}
                config={selectedConfig}
                onClose={() => {
                    setPlaygroundModalVisible(false);
                    setSelectedConfig(null);
                    setTestContentItem(null);
                }}
                onRunTest={handleRunTest}
                testLoading={playgroundLoading}
                testResult={testResult}
                onSaveConfig={handleSavePlaygroundConfig}
                saveLoading={savePlaygroundLoading}
                testContentItem={testContentItem}
                testContentLoading={testContentLoading}
                onSaveTestContent={handleSaveTestContent}
                saveTestContentLoading={saveTestContentLoading}
            />
        </div>
    );
}

export default AiProviderManagement;

